import { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Typography, alpha } from '@mui/material';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from 'api/client';
import { useAuth } from 'context/AuthContext';

const DEFAULT_MESSAGE = 'Scheduled platform maintenance is in progress.';

export default function MaintenanceGate({ children }) {
  const { isAdmin } = useAuth() || {};
  const [state, setState] = useState({ loading: true, enabled: false, message: DEFAULT_MESSAGE, error: '' });

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (isAdmin) {
        if (mounted) setState({ loading: false, enabled: false, message: DEFAULT_MESSAGE, error: '' });
        return;
      }

      try {
        const { data } = await api.get('/api/maintenance/status/');
        if (!mounted) return;
        setState({
          loading: false,
          enabled: Boolean(data?.enabled),
          message: data?.message || DEFAULT_MESSAGE,
          error: ''
        });
      } catch (err) {
        if (!mounted) return;
        setState({ loading: false, enabled: false, message: DEFAULT_MESSAGE, error: err?.message || '' });
      }
    };

    load();
    const timer = window.setInterval(load, 30000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [isAdmin]);

  if (isAdmin || (!state.loading && !state.enabled)) {
    return children;
  }

  if (state.loading) {
    return (
      <Box sx={{ minHeight: '52vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={1.5} alignItems="center">
          <CircularProgress size={28} sx={{ color: '#22c55e' }} />
          <Typography variant="body2" color="text.secondary">Checking platform availability...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '62vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'radial-gradient(circle at top, rgba(34,197,94,0.16), transparent 42%)'
            : 'radial-gradient(circle at top, rgba(34,197,94,0.12), transparent 42%)'
      }}
    >
      <Card
        sx={{
          width: 'min(680px, 100%)',
          borderRadius: 4,
          border: '1px solid rgba(34,197,94,0.42)',
          boxShadow: '0 24px 70px rgba(15, 23, 42, 0.18)',
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, rgba(15,23,42,0.96), rgba(15,23,42,0.88))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.94))'
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2.2} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                color: '#22c55e',
                bgcolor: alpha('#22c55e', 0.14),
                border: '1px solid rgba(34,197,94,0.34)'
              }}
            >
              <BuildCircleOutlinedIcon sx={{ fontSize: 34 }} />
            </Box>
            <Chip label="Maintenance Mode" sx={{ color: '#22c55e', bgcolor: alpha('#22c55e', 0.12), fontWeight: 800 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
                FuzzScan is temporarily unavailable
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {state.message}
              </Typography>
            </Box>
            <Alert severity="info" sx={{ width: '100%', textAlign: 'left', borderRadius: 2 }}>
              Administrators can still access the admin panel to disable maintenance mode.
            </Alert>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              sx={{ borderColor: '#22c55e', color: '#22c55e', fontWeight: 800 }}
            >
              Check Again
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
